import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import { useNavigate } from 'react-router-dom'
import { GuardianLoginError, useLoginMutation } from '../../api/auth'
import { isTurnstileConfigured } from '../../config/session'
import { useAuth } from '../../providers/auth/AuthProvider'

const DEFAULT_LOCKOUT_SECONDS = 60

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes > 0) {
    return `${minutes} phút ${seconds.toString().padStart(2, '0')} giây`
  }
  return `${seconds} giây`
}

const toE164 = (phone: string) => {
  const digits = phone.replace(/\D/g, '')
  if (digits.startsWith('84')) {
    return `+${digits}`
  }
  if (digits.startsWith('0')) {
    return `+84${digits.slice(1)}`
  }
  return `+84${digits}`
}

type Step = 'phone' | 'verify'

const Login: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, login } = useAuth()
  const [loginRequest, { isLoading }] = useLoginMutation()
  const turnstileRef = useRef<TurnstileInstance>(null)

  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [fullName, setFullName] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [error, setError] = useState('')
  const [lockoutRemaining, setLockoutRemaining] = useState(0)

  const turnstileEnabled = isTurnstileConfigured()

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/home', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    if (lockoutRemaining <= 0) {
      return
    }
    const timer = setInterval(() => {
      setLockoutRemaining((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [lockoutRemaining])

  const handleContinue = () => {
    setError('')
    if (phone.trim().replace(/\D/g, '').length < 9) {
      setError('Vui lòng nhập số điện thoại hợp lệ.')
      return
    }
    setStep('verify')
  }

  const handleVerify = async () => {
    setError('')

    if (lockoutRemaining > 0) {
      return
    }

    if (fullName.trim().length === 0 || birthYear.trim().length !== 4) {
      setError('Vui lòng nhập đầy đủ họ tên và năm sinh (4 chữ số) của một em.')
      return
    }

    if (turnstileEnabled && !turnstileToken) {
      setError('Vui lòng xác nhận không phải robot.')
      return
    }

    try {
      const response = await loginRequest({
        phone: toE164(phone),
        fullName: fullName.trim(),
        birthYear: Number(birthYear.trim()),
        turnstileToken: turnstileToken || undefined,
      }).unwrap()

      login(response.token, response.expiresAt)
      navigate('/home', { replace: true })
    } catch (e: any) {
      if (e?.status === 429) {
        const err = e as GuardianLoginError
        const seconds =
          err.retryAfterSeconds && err.retryAfterSeconds > 0
            ? err.retryAfterSeconds
            : DEFAULT_LOCKOUT_SECONDS
        setLockoutRemaining(seconds)
      } else if (typeof e?.data === 'string' && e.data) {
        setError(e.data)
      } else {
        setError('Đăng nhập không thành công. Vui lòng thử lại.')
      }
      turnstileRef.current?.reset()
      setTurnstileToken('')
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h5" fontWeight={700}>
          Xứ đoàn Nữ Vương Hòa Bình
        </Typography>

        {step === 'phone' && (
          <>
            <Typography color="text.secondary">
              Đăng nhập bằng số điện thoại phụ huynh đã đăng ký với giáo xứ.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Số điện thoại"
              placeholder="0xxxxxxxxx"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              autoFocus
              type="tel"
              inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
              onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
            />

            <Button variant="contained" size="large" onClick={handleContinue}>
              Tiếp tục
            </Button>
          </>
        )}

        {step === 'verify' && (
          <>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography color="text.secondary">Số điện thoại: <b>{phone}</b></Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setStep('phone')
                  setError('')
                }}
              >
                Đổi số
              </Link>
            </Stack>

            <Typography color="text.secondary">
              Để xác thực, nhập họ tên và năm sinh của <b>một</b> đoàn sinh thuộc số điện thoại này.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            {lockoutRemaining > 0 && (
              <Alert severity="warning">
                Bạn đã thử quá nhiều lần. Vui lòng thử lại sau{' '}
                <b>{formatCountdown(lockoutRemaining)}</b>.
              </Alert>
            )}

            {!turnstileEnabled && process.env.NODE_ENV === 'development' && (
              <Alert severity="info">
                Chế độ dev: Turnstile chưa cấu hình — backend bỏ qua xác minh robot.
              </Alert>
            )}

            <TextField
              label="Họ tên đoàn sinh"
              placeholder="Ví dụ: Nguyễn Văn An"
              helperText="Vui lòng nhập chính xác họ tên của em."
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              autoFocus
            />

            <TextField
              label="Năm sinh đoàn sinh"
              placeholder="Ví dụ: 2015"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              fullWidth
              type="number"
              inputProps={{ inputMode: 'numeric', maxLength: 4, min: 1900, max: 2100 }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
            />

            {turnstileEnabled && (
              <Box>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY!}
                  options={{ action: 'guardian_login' }}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                />
              </Box>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleVerify}
              disabled={isLoading || lockoutRemaining > 0}
            >
              {isLoading
                ? 'Đang xác thực...'
                : lockoutRemaining > 0
                ? `Thử lại sau ${formatCountdown(lockoutRemaining)}`
                : 'Xác thực & Đăng nhập'}
            </Button>
          </>
        )}
      </Stack>
    </Container>
  )
}

export default Login
